import { useEffect, useState } from 'react';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';

type Listener = () => void;

type CompatibleEditorEntry = {
	/** UUID used to register the editor with the rovo content bridge (routing key). */
	editorId: string;
	/** Pre-formed container ARI for the duplicate remix API call (e.g. Jira issue ARI). */
	containerAri?: string;
};

let compatibleEditors: CompatibleEditorEntry[] = [];
const listeners = new Set<Listener>();
autoBeforeEachTest(() => {
	listeners.clear();
});

const emit = (): void => {
	for (const listener of listeners) {
		listener();
	}
};

const subscribe = (listener: Listener): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export const getActiveCompatibleEditorId = (): string | undefined =>
	compatibleEditors[compatibleEditors.length - 1]?.editorId;

export const registerCompatibleEditor = (editorId: string, containerAri?: string): (() => void) => {
	compatibleEditors = [
		...compatibleEditors.filter((e) => e.editorId !== editorId),
		{ editorId, containerAri },
	];
	emit();
	return () => {
		compatibleEditors = compatibleEditors.filter((e) => e.editorId !== editorId);
		emit();
	};
};

/**
 * Returns the full active compatible editor entry (editorId + containerAri) in a single
 * subscription. Prefer this over separate hooks to avoid double re-renders on a single
 * registry update.
 */
export const useActiveCompatibleEditor = (): CompatibleEditorEntry | undefined => {
	const getActive = () => compatibleEditors[compatibleEditors.length - 1];
	const [activeEditor, setActiveEditor] = useState<CompatibleEditorEntry | undefined>(getActive);

	useEffect(() => {
		return subscribe(() => {
			setActiveEditor(getActive());
		});
	}, []);

	return activeEditor;
};

export const resetCompatibleEditorsRegistryForTests = (): void => {
	compatibleEditors = [];
	emit();
};
